import type { Room } from '@modelcast/types';
import { SwitchControl } from '../../components/SwitchControl';

interface PublishSectionProps {
  room: Room | null;
  draft: {
    isPublished: boolean;
    isPublic: boolean;
    isModelAdPublished: boolean;
  };
  hasChanges: boolean;
  onUpdateDraft: (updates: Partial<{ isPublished: boolean; isPublic: boolean; isModelAdPublished: boolean }>) => void;
  onSave: () => Promise<void>;
  isSubmitting: boolean;
  onBack: () => void;
}

export function PublishSection({
  room,
  draft,
  hasChanges,
  onUpdateDraft,
  onSave,
  isSubmitting,
  onBack,
}: PublishSectionProps) {
  return (
    <div className="flex-1 flex justify-center bg-bg">
      <div className="w-full max-w-[640px] py-8 sm:py-10 lg:py-[72px] flex flex-col gap-7">
        {/* Header */}
        <div>
          <h1 className="type-title-page text-text">
            Publish
          </h1>
          <p className="type-body text-text-dim mt-3.5">
            Control your event's visibility and listing status.
          </p>
        </div>

        {/* Publishing Controls */}
        <div className="flex flex-col gap-3">
          {/* Publish Toggle */}
          <div className="bg-panel rounded-xl border border-line p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="type-body-sm text-text">Publish event</div>
                <div className="type-body-sm text-text-dim mt-0.5">
                  {draft.isPublished
                    ? 'Event is live and tickets are on sale'
                    : 'The event cannot be viewed by others'}
                </div>
              </div>
              <SwitchControl
                checked={draft.isPublished}
                onChange={(isPublished) => onUpdateDraft({ isPublished })}
                disabled={isSubmitting || !room}
                ariaLabel="Publish event"
              />
            </div>
          </div>

          {/* Public Listing Toggle - always visible */}
          <div className="bg-panel rounded-xl border border-line p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="type-body-sm text-text">List publicly on homepage</div>
                <div className="type-body-sm text-text-dim mt-0.5">
                  {draft.isPublic ? 'Anyone can find this event' : 'Only people with the link can see it'}
                </div>
              </div>
              <SwitchControl
                checked={draft.isPublic}
                onChange={(isPublic) => onUpdateDraft({ isPublic })}
                disabled={isSubmitting || !room}
                ariaLabel="List publicly on homepage"
              />
            </div>
          </div>

          {room?.modelOption === 'ad' && (
            <div className="bg-panel rounded-xl border border-line p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="type-body-sm text-text">Publish my ad</div>
                  <div className="type-body-sm text-text-dim mt-0.5">
                    {draft.isModelAdPublished
                      ? 'Models can see this job and apply'
                      : 'Your ad is saved, but hidden from models'}
                  </div>
                </div>
                <SwitchControl
                  checked={draft.isModelAdPublished}
                  onChange={(isModelAdPublished) => onUpdateDraft({ isModelAdPublished })}
                  disabled={isSubmitting || !room}
                  ariaLabel="Publish my ad"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col gap-2.5 mt-auto pt-6 border-t border-line sm:flex-row sm:items-center">
          <button onClick={onBack} className="btn w-full sm:w-auto">
            ← Back
          </button>
          <div className="hidden flex-1 sm:block" />
          <button
            type="button"
            onClick={onSave}
            disabled={isSubmitting || !hasChanges || !room}
            className="btn btn-accent btn-lg w-full disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
